/**
 * Times-collection layer — the field/item module for the `record_times` grid,
 * parallel to `records.ts` / `listings.ts`. Owns the item type and the grid's
 * column fields, the single layer feeding a time collection its items + fields.
 */

import { type AugerField, type EnrichableField, getFieldType } from "../fields/fields.js"
import { type BaseItem } from "./item.js"

/**
 * A time's value rules, checked before any write: it can't end before it
 * starts, and a capacity can't be negative. `record_times` also enforces the
 * first with CHECK (end_at >= start_at), which covers a patch naming only one
 * end; checking here names the problem instead of a constraint. Takes the
 * column names; checks only what `values` carries.
 */
export function assertTimeValues(
  values: { start_at?: unknown; end_at?: unknown; capacity?: unknown },
  source: string
): void {
  const { start_at, end_at, capacity } = values
  if (start_at != null && end_at != null) {
    const start = new Date(start_at as string).getTime()
    const end = new Date(end_at as string).getTime()
    if (end < start) {
      throw new Error(`${source}: a time can't end (${end_at}) before it starts (${start_at})`)
    }
  }
  if (typeof capacity === "number" && capacity < 0) {
    throw new Error(`${source}: capacity can't be negative (got ${capacity})`)
  }
}

/** A `record_times` row as the times grid reads it. The `record` reference
 *  column carries both the stored id (`record_id`) and its resolved entity
 *  (`record`) — the resolved one is `value` (display), the id is the raw value
 *  the editor binds. Open contract: extra joined columns may ride along per
 *  query. */
export type TimeItem = BaseItem & {
  id: number
  record_id?: number
  record?: unknown
  time_type?: string
  title?: string | null
  start_at?: string | null
  end_at?: string | null
  all_day?: boolean | null
  timezone?: string | null
  created_at?: string
  updated_at?: string
}

const row = (item: unknown) => item as TimeItem

/** Column specs for the times grid. The `record` column splits `value`
 *  (resolved entity, for display) from `rawValue` (the stored id the editor
 *  binds); scalar columns leave `rawValue` to default to `value`. */
const FIELDS: ReadonlyArray<EnrichableField> = [
  { key: "id", label: "ID", type: "number", width: 70, value: (i) => row(i).id },
  {
    key: "_record",
    label: "Record",
    type: "record",
    width: 200,
    value: (i) => row(i).record,
    rawValue: (i) => row(i).record_id,
  },
  {
    key: "time_type",
    label: "Type",
    type: "select",
    width: 120,
    values: ["instant", "event", "hours", "slot", "lifetime"],
    value: (i) => row(i).time_type,
  },
  {
    key: "title",
    label: "Title",
    type: "string",
    width: 180,
    isEditable: true,
    value: (i) => row(i).title || "",
  },
  {
    key: "start_at",
    label: "Start",
    type: "timestamp",
    width: 180,
    isEditable: true,
    value: (i) => row(i).start_at,
  },
  {
    key: "end_at",
    label: "End",
    type: "timestamp",
    width: 180,
    isEditable: true,
    value: (i) => row(i).end_at,
  },
  {
    key: "all_day",
    label: "All Day",
    type: "boolean",
    width: 80,
    isEditable: true,
    value: (i) => row(i).all_day,
  },
  {
    key: "timezone",
    label: "Timezone",
    type: "string",
    width: 160,
    isEditable: true,
    value: (i) => row(i).timezone || "",
  },
  {
    key: "created_at",
    label: "Created",
    type: "timestamp",
    width: 180,
    value: (i) => row(i).created_at,
  },
]

/** The times grid's enriched fields: each spec's FieldType chain resolved (so it
 *  carries that type's format/parse/operators) and `rawValue` guaranteed —
 *  defaulting to `value` for scalar columns that don't split id from entity. */
export const timeFields: AugerField[] = FIELDS.map(
  (f) =>
    ({
      ...getFieldType(f.type),
      ...f,
      rawValue: f.rawValue ?? f.value,
    }) as AugerField
)
